import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { SubscriptionType } from "../../../../Data/subscription.dto";
import {
  X,
  Sparkles,
  CreditCard,
  IndianRupee,
  Calendar,
  Loader2,
  ShoppingCart,
} from "lucide-react";

interface PopupProps {
  Buy: (id: number, duration: number) => void;
  cancel: () => void;
  card: SubscriptionType;
  loading: Boolean;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const formatToINR = (amount: number): string =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

const GetDurationPopup: React.FC<PopupProps> = ({ Buy, cancel, card, loading }) => {
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly");

  const totalPay =
    selected === "yearly"
      ? Number(card.storage_price_per_year ?? 0)
      : card.offer_price;

  return (
    <Overlay onClick={cancel}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              <CreditCard size={22} />
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Checkout
              </HeroBadge>
              <ModalTitle>Choose billing period</ModalTitle>
              <ModalSubtitle>
                Select how you&apos;d like to pay for your plan.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={cancel} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </ModalHero>

        <ModalBody>
          <PlanChip>
            <CreditCard size={14} />
            Selected plan: <strong>{card.name}</strong>
          </PlanChip>

          <SummaryGrid>
            <SummaryItem>
              <SummaryLabel>Price per month</SummaryLabel>
              <SummaryValue>{formatToINR(card.offer_price)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Storage</SummaryLabel>
              <SummaryValue>{card.storage_size}</SummaryValue>
            </SummaryItem>
          </SummaryGrid>

          <SectionLabel>
            <Calendar size={12} />
            Billing period
          </SectionLabel>
          <DurationRow>
            <DurationChip
              type="button"
              $active={selected === "yearly"}
              onClick={() => setSelected("yearly")}
            >
              Yearly
              <DurationHint>Best value</DurationHint>
            </DurationChip>
            <DurationChip
              type="button"
              $active={selected === "monthly"}
              onClick={() => setSelected("monthly")}
            >
              Monthly
            </DurationChip>
          </DurationRow>

          <TotalBox>
            <TotalLabel>
              <IndianRupee size={14} />
              Total payable
            </TotalLabel>
            <TotalValue>{formatToINR(totalPay)}</TotalValue>
            <TotalHint>
              {selected === "yearly" ? "Billed annually" : "Billed monthly"}
            </TotalHint>
          </TotalBox>

          {loading ? (
            <LoadingWrap>
              <Spinner size={28} color="#c94daa" />
              Processing payment…
            </LoadingWrap>
          ) : (
            <ActionRow>
              <CancelBtn type="button" onClick={cancel}>
                Cancel
              </CancelBtn>
              <SubmitBtn
                type="button"
                onClick={() => Buy(card.id, selected === "yearly" ? 12 : 1)}
              >
                <ShoppingCart size={16} />
                Buy now
              </SubmitBtn>
            </ActionRow>
          )}
        </ModalBody>
      </ModalBox>
    </Overlay>
  );
};

export default GetDurationPopup;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 9999;
  padding: 16px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
`;

const PlanChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.15);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.72);
  strong {
    font-weight: 700;
    color: #171717;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 18px;
`;

const SummaryItem = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const SummaryLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const DurationRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
`;

const DurationChip = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid
    ${(p) => (p.$active ? "rgba(201, 77, 170, 0.35)" : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.08)" : "#fafafa")};
  color: ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.65)")};
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.3);
    background: rgba(201, 77, 170, 0.06);
  }
`;

const DurationHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #31a66a;
`;

const TotalBox = styled.div`
  padding: 18px 20px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.12) 0%,
    rgba(206, 86, 176, 0.1) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.16);
  margin-bottom: 4px;
`;

const TotalLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const TotalValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const TotalHint = styled.div`
  margin-top: 4px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 100px;
  margin-top: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const SubmitBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover {
    transform: translateY(-1px);
  }
`;
